import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, User, FileText, ArrowRight, Paperclip, Activity, FileCheck, Printer, CheckCircle, Lock, ShieldCheck } from 'lucide-react';
import useApi from '../hooks/useApi';
import { useSettings } from '../context/SettingsContext';
import { formatDate, formatDateTime } from '../utils/helpers';

const shouldHideTimelineEntry = (action = '') => ['created', 'updated'].includes(String(action).trim().toLowerCase());

const CaseHistory = ({ caseType, caseId, onPageClick }) => {
  const { get } = useApi();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const isEfileEnabled = settings?.efile_enabled == 1 || settings?.efile_enabled === 'true' || settings?.efile_enabled === true;
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline' or 'greensheet'

  useEffect(() => {
    if (caseId) {
      fetchHistory();
    }
  }, [caseId, caseType]);

  useEffect(() => {
    if (!isEfileEnabled && viewMode === 'greensheet') setViewMode('timeline');
  }, [isEfileEnabled, viewMode]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await get(`/movements/${caseType}/${caseId}`);
      if (response && response.success) {
        const visibleHistory = Array.isArray(response.data)
          ? response.data.filter((entry) => !shouldHideTimelineEntry(entry?.action))
          : [];
        setHistory(visibleHistory);
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
      setError('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const formatDisplayName = (value, fallback = 'System') => {
    const clean = String(value || '').trim();
    if (!clean) return fallback;
    return clean;
  };

  const toSafeDate = (value) => {
    if (!value) return null;
    const clean = String(value).trim();
    if (!clean) return null;

    const normalized = clean.includes(' ') ? clean.replace(' ', 'T') : clean;
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const formatTimelineDate = (value) => {
    const d = toSafeDate(value);
    if (!d) return '';
    return `${d.getDate()} ${d.toLocaleString('en-US', { month: 'short' })}`;
  };

  const formatTimelineYear = (value) => {
    const d = toSafeDate(value);
    if (!d) return '';
    return d.getFullYear();
  };

  const formatTimelineTime = (value) => {
    const d = toSafeDate(value);
    if (!d) return '';
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }).replace(/^\s+|\s+$/g, '');
  };

  const resolveHistoryActionLabel = (item) => {
    const action = String(item?.action || '').trim();
    const status = String(item?.status || '').trim();
    const toName = formatDisplayName(item?.toUserName || item?.assignedToName || item?.forwardedToName || item?.targetName, 'Office');
    const fromName = formatDisplayName(item?.fromUserName || item?.createdByName || item?.sourceName, 'System');

    if (!action) return status ? `Status: ${status}` : 'Case update';
    const lower = action.toLowerCase();
    if (lower.includes('forward')) return status ? `Forwarded • ${status}` : `Forwarded to ${toName}`;
    if (lower.includes('assign')) return status ? `Assigned • ${status}` : `Assigned to ${toName}`;
    if (lower.includes('receive') || lower.includes('received')) return status ? `Received • ${status}` : `Received by ${toName}`;
    if (lower.includes('return')) return status ? `Returned • ${status}` : `Returned by ${fromName}`;
    if (lower.includes('approve')) return status ? `Approved • ${status}` : `Approved by ${fromName}`;
    if (lower.includes('reject')) return status ? `Rejected • ${status}` : `Rejected by ${fromName}`;
    if (lower.includes('compliance')) return status ? `Compliance • ${status}` : 'Compliance update';
    return status ? `${action} • ${status}` : action.charAt(0).toUpperCase() + action.slice(1);
  };

  const renderHash = (hash, signature) => {
    if (!hash && !signature) return null;

    return (
      <div className="flex items-center gap-2 mt-3 text-xs">
        {hash && (
          <div className="flex items-center gap-1 text-gray-500 bg-gray-50 px-2.5 py-1 rounded border border-gray-200" title={`SHA-256 Hash: ${hash}`}>
            <Lock className="w-3 h-3" />
            <span className="font-mono">{hash.substring(0, 8)}...{hash.substring(hash.length - 8)}</span>
          </div>
        )}
        {signature && (
          <div className="flex items-center gap-1 text-green-700 bg-green-50 px-2.5 py-1 rounded border border-green-200" title="Digitally Signed">
            <ShieldCheck className="w-3 h-3" />
            <span>Signed</span>
          </div>
        )}
      </div>
    );
  };

  const getActionTone = (action = '', status = '') => {
    const lowerAction = String(action).toLowerCase();
    const lowerStatus = String(status).toLowerCase();

    if (lowerStatus.includes('complete') || lowerStatus.includes('closed') || lowerStatus.includes('approved')) {
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    }
    if (lowerStatus.includes('progress') || lowerStatus.includes('pending') || lowerStatus.includes('in progress')) {
      return 'bg-amber-50 text-amber-700 border-amber-200';
    }
    if (lowerAction.includes('forward') || lowerAction.includes('assign') || lowerAction.includes('review')) {
      return 'bg-indigo-50 text-indigo-700 border-indigo-200';
    }
    if (lowerAction.includes('compl') || lowerAction.includes('return') || lowerAction.includes('revert')) {
      return 'bg-rose-50 text-rose-700 border-rose-200';
    }
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const getDotTone = (action = '', status = '') => {
    const lowerAction = String(action).toLowerCase();
    const lowerStatus = String(status).toLowerCase();

    if (lowerStatus.includes('complete') || lowerStatus.includes('closed') || lowerStatus.includes('approved')) return 'bg-emerald-500';
    if (lowerAction.includes('forward') || lowerAction.includes('assign')) return 'bg-indigo-500';
    if (lowerAction.includes('compl') || lowerAction.includes('return') || lowerAction.includes('revert')) return 'bg-rose-500';
    if (lowerStatus.includes('progress') || lowerStatus.includes('pending')) return 'bg-amber-500';
    return 'bg-slate-500';
  };

  const groupedHistory = history.reduce((acc, item) => {
    const dateKey = item?.createdAt ? String(item.createdAt).slice(0, 10) : 'Unknown';
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(item);
    return acc;
  }, {});

  const summaryCards = [
    {
      label: 'Entries',
      value: String(history.length),
      icon: Activity,
      tone: 'bg-indigo-50 text-indigo-700'
    },
    {
      label: 'Latest Status',
      value: String(history[0]?.status || 'Active'),
      icon: FileCheck,
      tone: 'bg-emerald-50 text-emerald-700'
    },
    {
      label: 'Latest Actor',
      value: formatDisplayName(history[0]?.fromUserName || history[0]?.createdByName || history[0]?.sourceName, 'System'),
      icon: User,
      tone: 'bg-sky-50 text-sky-700'
    }
  ];

  const handlePrint = () => {
    const url = `/print/history/${caseType}/${caseId}`;
    window.open(url, '_blank', 'width=800,height=600');
  };

  if (loading) return <div className="text-center py-6 text-gray-500">Loading history...</div>;
  if (error) return <div className="text-center py-6 text-red-500">{error}</div>;
  if (!history || history.length === 0) return <div className="text-center py-6 text-gray-500">No history available</div>;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 md:p-6 mt-6 overflow-hidden">
      <div className="mb-6 rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 p-4 text-white shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-white/10 p-2 text-indigo-100 ring-1 ring-white/10">
              <Activity className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-slate-300">Official Register</div>
              <h3 className="text-xl font-semibold text-white">Case History</h3>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setViewMode('timeline')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                viewMode === 'timeline'
                  ? 'bg-white text-slate-900'
                  : 'bg-white/10 text-slate-100 hover:bg-white/15'
              }`}
            >
              Timeline
            </button>
            {isEfileEnabled && (
              <button
                onClick={() => setViewMode('greensheet')}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  viewMode === 'greensheet'
                    ? 'bg-emerald-500 text-white'
                    : 'bg-white/10 text-slate-100 hover:bg-white/15'
                }`}
              >
                Green Sheet
              </button>
            )}
            <button
              onClick={handlePrint}
              className="p-2 text-slate-100 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              title="Print History"
            >
              <Printer className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        {summaryCards.map(({ label, value, icon: Icon, tone }) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">{label}</p>
                <p className="mt-2 text-base font-semibold text-slate-900 truncate">{value}</p>
              </div>
              <span className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}>
                <Icon className="w-4 h-4" />
              </span>
            </div>
          </div>
        ))}
      </div>

      {viewMode === 'timeline' || !isEfileEnabled ? (
        <div className="relative rounded-2xl border border-slate-200 bg-slate-50/70 p-4 sm:p-5 shadow-inner shadow-slate-100/80">
          <div className="absolute left-[71px] top-6 bottom-6 w-px bg-slate-300" />

          <div className="space-y-6">
            {Object.entries(groupedHistory).map(([dateKey, items]) => (
              <div key={dateKey} className="space-y-5">
                <div className="ml-[20px] inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 shadow-sm">
                  <Clock className="w-3.5 h-3.5 text-slate-500" />
                  {dateKey === 'Unknown' ? 'Unspecified date' : new Date(dateKey).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>

                {items.map((item, index) => {
                  const actionLabel = resolveHistoryActionLabel(item);
                  const fromName = formatDisplayName(item?.fromUserName || item?.createdByName || item?.sourceName, 'System');
                  const toName = formatDisplayName(item?.toUserName || item?.assignedToName || item?.forwardedToName || item?.targetName, 'Office');
                  const dateText = formatTimelineDate(item.createdAt);
                  const yearText = formatTimelineYear(item.createdAt);
                  const timeText = formatTimelineTime(item.createdAt);
                  const statusText = String(item?.status || '').trim();
                  const noteText = String(item?.remarks || item?.notes || '').trim();
                  const accentTone = getActionTone(item?.action, item?.status);
                  const dotTone = getDotTone(item?.action, item?.status);

                  return (
                    <div key={item.id || `${dateKey}-${index}`} className="relative grid grid-cols-[94px_minmax(0,1fr)] gap-4 items-start">
                      <div className="relative pt-3 pr-2 text-right text-slate-700">
                        <div className="text-[15px] font-semibold leading-tight">{dateText}</div>
                        <div className="text-[11px] text-slate-500 mt-1 flex items-center justify-end gap-1 leading-tight">
                          <Clock className="w-3 h-3" />
                          {timeText || '00:00'}
                        </div>
                        {yearText && (<div className="text-[11px] text-slate-500 mt-0.5">{yearText}</div>)}
                      </div>

                      <div className="relative pl-6">
                        <span className={`absolute left-0 top-5 -translate-x-1/2 w-4 h-4 rounded-full border-2 border-white z-10 shadow-sm ${dotTone}`} />

                        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex flex-wrap items-center gap-2 mb-3">
                            <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[12px] font-semibold uppercase tracking-[0.12em] ${accentTone}`}>
                              {actionLabel}
                            </span>
                            {statusText && (
                              <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                                {statusText}
                              </span>
                            )}
                            {item.department && (
                              <span className="text-[11px] text-slate-500">{item.department}</span>
                            )}
                          </div>

                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-center gap-3 text-[15px] font-medium text-slate-800">
                              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-rose-100 text-[12px] font-bold text-rose-700">
                                {fromName.charAt(0).toUpperCase()}
                              </span>
                              <span>{fromName}</span>
                              <ArrowRight className="w-4 h-4 text-slate-400" />
                              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-[12px] font-bold text-emerald-700">
                                {toName.charAt(0).toUpperCase()}
                              </span>
                              <span>{toName}</span>
                            </div>

                            {item?.action && (
                              <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400">{String(item.action).trim()}</span>
                            )}
                          </div>

                          {noteText && (
                            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                              <div className="flex items-start gap-2 text-sm text-slate-700">
                                <Paperclip className="w-3.5 h-3.5 text-slate-500 mt-0.5" />
                                <span className="font-mono text-[12px] break-all leading-relaxed">{noteText}</span>
                              </div>
                            </div>
                          )}

                          {renderHash(item.hash, item.digitalSignature)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-green-50/50 rounded-lg border border-green-100 p-6 min-h-[400px]">
          <div className="text-center text-gray-500 mt-10">
            <FileText className="w-12 h-12 text-green-300 mx-auto mb-3" />
            <h4 className="text-lg font-medium text-gray-900">Green Sheet View</h4>
            <p className="max-w-md mx-auto mt-2">
              This view mimics the physical green sheet, showing the chain of custody and notes in a formal format.
            </p>
            <button 
              onClick={() => navigate(`/green-sheet/${caseType === 'receipt' ? 'receipt' : 'dispatch'}/${caseId}`)}
              className="mt-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
            >
              Open Full Green Sheet
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseHistory;
